import { XMarkIcon } from '@heroicons/react/20/solid';
import { ArrowUpTrayIcon, DocumentIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { Rfq } from 'src/shared/types/rfq';
import { UploadedFile } from 'src/shared/types/uploadedFile';
import {
  isImageMimeType,
  isUnprocessableForRfqExtraction
} from 'src/shared/utils/fileTypeUtils';

import { cn, Progress } from 'src/shared/components/ui';
import { ButtonV2, Typography } from 'src/shared/components/ui';

import { useCaptionModal } from '../../hooks/useCaptionModal';
import { useFileCaptionManager } from '../../hooks/useFileCaptionManager';
import { useFilePreview } from '../../hooks/useFilePreview';
import { useOptimisticFileUploadWithCaption } from '../../hooks/useOptimisticFileUploadWithCaption';
import { FilePreview } from './FilePreview';
import { ImagePreview } from './ImagePreview';
import { InlineCaptionOverlay } from './InlineCaptionOverlay';
import { UnprocessableFileWarning } from './UnprocessableFileWarning';
import { UploadFailedNotice } from './UploadFailedNotice';

interface AddReferencesUploadTabProps {
  rfq?: Rfq;
  /** Callback when files are selected and ready to submit */
  onFilesSelected: (files: UploadedFile[]) => void;
  onClose: () => void;
  /**
   * Files to seed into the tab automatically — used when the parent (chat)
   * forwards a window-level drag-and-drop into the modal. Each non-empty
   * value is consumed once.
   */
  initialFiles?: File[];
  /** Called after `initialFiles` have been seeded so the parent can clear them. */
  onInitialFilesConsumed?: () => void;
}

interface FileWithCaption {
  file: File;
  caption: string;
  preview: string;
  id: string;
  pendingFileId: string;
  source: 'user' | 'brave';
}

/**
 * File upload tab content for the AddReferencesModal.
 * Allows uploading files via drag-and-drop or file picker, adding captions, and submitting.
 * Adapted from RfqFilesUpload for use in a modal context (builder mode only).
 */
const AddReferencesUploadTab = ({
  rfq,
  onFilesSelected,
  onClose,
  initialFiles,
  onInitialFilesConsumed
}: AddReferencesUploadTabProps) => {
  const [files, setFiles] = useState<FileWithCaption[]>([]);
  const [pendingFileUploads, setPendingFileUploads] = useState<
    FileWithCaption[]
  >([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const filesRef = useRef<FileWithCaption[]>([]);
  const [newFileIds, setNewFileIds] = useState<Set<string>>(new Set());
  const [isShowingNewFilesOnly, setIsShowingNewFilesOnly] = useState(false);
  const pendingEditFileIdRef = useRef<string | null>(null);

  // Reusable hooks
  const queryClient = useQueryClient();
  const { setPreview, getPreview, removePreview, previewUrlsRef } =
    useFilePreview();
  const uploadHook = useOptimisticFileUploadWithCaption({
    rfqId: rfq?.rfq?.id
  });
  const {
    pendingFiles,
    addFiles: addFilesToUpload,
    removeFile: removeFileFromUpload,
    waitForUploads,
    isUploading: isUploadingFiles,
    uploadFileWithCaption,
    retryUpload
  } = uploadHook;

  const { getPendingFileByIdOrFile, updateCaptionForFile } =
    useFileCaptionManager(rfq?.rfq?.id, uploadHook);

  // Filter files for modal
  const modalItems = useMemo(() => {
    if (pendingFileUploads.length > 0) {
      return pendingFileUploads;
    }
    if (isShowingNewFilesOnly && newFileIds.size > 0) {
      return files.filter((f) => newFileIds.has(f.id));
    }

    return files;
  }, [files, isShowingNewFilesOnly, newFileIds, pendingFileUploads]);

  const captionModal = useCaptionModal(modalItems);
  const {
    isCaptionModalOpen,
    onCloseCaptionModal,
    currentCaptionIndex,
    captionValue,
    setCaptionValue,
    isEditMode,
    handleEditCaption,
    handleCaptionNext: captionNext,
    handleCaptionPrevious: captionPrevious,
    handleCaptionDone: captionDone,
    openModalForNewItems
  } = captionModal;

  // Keep filesRef in sync
  useEffect(() => {
    filesRef.current = files;
    files.forEach((f) => {
      if (f.preview && !previewUrlsRef.current.has(f.id)) {
        setPreview(f.id, f.preview);
      }
    });
  }, [files, setPreview]);

  // Handle opening modal for existing file after clearing new files flag
  useEffect(() => {
    if (pendingEditFileIdRef.current && !isShowingNewFilesOnly) {
      const fileId = pendingEditFileIdRef.current;
      const fileIndex = files.findIndex((f) => f.id === fileId);

      if (fileIndex !== -1) {
        handleEditCaption(fileIndex);
      }
      pendingEditFileIdRef.current = null;
    }
  }, [isShowingNewFilesOnly, files, handleEditCaption]);

  // Sync files with pendingFiles from hook
  useEffect(() => {
    setFiles((prevFiles) => {
      if (prevFiles.length === 0) return prevFiles;

      const existingPendingIds = new Set(
        prevFiles.filter((f) => f.pendingFileId).map((f) => f.pendingFileId!)
      );
      const newPendingFiles = pendingFiles.filter(
        (pf) => !existingPendingIds.has(pf.id)
      );

      let hasChanges = false;
      const result: FileWithCaption[] = [];

      for (const f of prevFiles) {
        const previewUrl = getPreview(f.id) || f.preview;

        if (previewUrl && !previewUrlsRef.current.has(f.id)) {
          setPreview(f.id, previewUrl);
        }

        let updatedFile: FileWithCaption = {
          file: f.file,
          caption: f.caption,
          preview: previewUrl,
          id: f.id,
          pendingFileId: f.pendingFileId,
          source: f.source
        };

        if (!updatedFile.pendingFileId) {
          const matchingPendingFile = newPendingFiles.find(
            (pf) =>
              pf.file === f.file ||
              (pf.file.name === f.file.name &&
                pf.file.size === f.file.size &&
                pf.file.lastModified === f.file.lastModified)
          );

          if (matchingPendingFile) {
            hasChanges = true;
            updatedFile = {
              ...updatedFile,
              pendingFileId: matchingPendingFile.id
            };
          }
        }

        if (updatedFile.pendingFileId) {
          const pendingFile = pendingFiles.find(
            (pf) => pf.id === updatedFile.pendingFileId
          );

          if (pendingFile && pendingFile.caption !== updatedFile.caption) {
            hasChanges = true;
            updatedFile = {
              ...updatedFile,
              caption: pendingFile.caption
            };
          }
        }

        result.push(updatedFile);
      }

      if (!hasChanges && result.length === prevFiles.length) {
        return prevFiles;
      }

      if (result.length !== prevFiles.length) {
        return prevFiles;
      }

      return result;
    });
  }, [pendingFiles, getPreview, setPreview, previewUrlsRef]);

  const handleFileSelect = (selectedFiles: FileList | File[] | null) => {
    if (!selectedFiles) return;
    const fileArray = Array.from(selectedFiles);

    if (fileArray.length === 0) return;

    const timestamp = Date.now();
    const newPendingFiles: FileWithCaption[] = fileArray.map((file, index) => {
      const fileId = `${file.name}-${file.size}-${file.lastModified}-${timestamp}-${index}-${Math.random()}`;
      const preview = URL.createObjectURL(file);

      setPreview(fileId, preview);

      return {
        file,
        caption: '',
        preview,
        id: fileId,
        pendingFileId: '',
        source: 'user'
      };
    });

    setPendingFileUploads(newPendingFiles);
    openModalForNewItems(0);
  };

  // Seed externally-provided files (e.g. window drag-and-drop forwarded from the
  // RFQ builder chat) exactly once per array reference, then notify the parent
  // so it can clear its state.
  const seededInitialFilesRef = useRef<File[] | null>(null);

  useEffect(() => {
    if (!initialFiles || initialFiles.length === 0) return;
    if (seededInitialFilesRef.current === initialFiles) return;
    seededInitialFilesRef.current = initialFiles;
    handleFileSelect(initialFiles);
    onInitialFilesConsumed?.();
    // handleFileSelect is intentionally omitted; the ref guard makes this a
    // one-shot effect per new initialFiles reference.
  }, [initialFiles]);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleRemoveFile = useCallback(
    (index: number) => {
      const fileToRemove = files[index];

      removePreview(fileToRemove.id);

      if (fileToRemove.pendingFileId) {
        removeFileFromUpload(fileToRemove.pendingFileId);
      }

      const newFiles = files.filter((_, i) => i !== index);

      setFiles(newFiles);

      if (currentCaptionIndex >= newFiles.length && newFiles.length > 0) {
        captionModal.setCurrentCaptionIndex(newFiles.length - 1);
      } else if (newFiles.length === 0) {
        onCloseCaptionModal();
      }
    },
    [
      files,
      currentCaptionIndex,
      removeFileFromUpload,
      removePreview,
      captionModal,
      onCloseCaptionModal
    ]
  );

  const getPendingFile = useCallback(
    (pendingFileId: string) => {
      return pendingFiles.find((pf) => pf.id === pendingFileId);
    },
    [pendingFiles]
  );

  // Add pending file to files and start upload
  const addPendingFile = useCallback(async () => {
    if (pendingFileUploads.length === 0) return;

    const currentPendingFile = pendingFileUploads[currentCaptionIndex];

    if (!currentPendingFile || !currentPendingFile.file) return;

    const fileToAdd = currentPendingFile.file;

    addFilesToUpload([fileToAdd]);

    const fileToAddToState = {
      ...currentPendingFile,
      caption: captionValue
    };

    setFiles((prev) => [...prev, fileToAddToState]);
    setPendingFileUploads((prev) =>
      prev.filter((_, index) => index !== currentCaptionIndex)
    );

    const updatePendingFileCaption = async (retries = 5) => {
      for (let i = 0; i < retries; i++) {
        const pendingFile = getPendingFileByIdOrFile(undefined, fileToAdd);

        if (pendingFile) {
          await updateCaptionForFile(pendingFile, captionValue).catch(() => {});
          if (!captionValue.trim() && pendingFile.status === 'pending') {
            await uploadFileWithCaption(pendingFile.id, captionValue).catch(
              () => {}
            );
          }

          return;
        }
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
    };

    updatePendingFileCaption();
  }, [
    pendingFileUploads,
    currentCaptionIndex,
    captionValue,
    addFilesToUpload,
    getPendingFileByIdOrFile,
    updateCaptionForFile,
    uploadFileWithCaption
  ]);

  // Caption modal handlers
  const handleCaptionSave = useCallback(async () => {
    if (pendingFileUploads.length > 0) {
      return;
    }

    const currentModalFile = modalItems[currentCaptionIndex];

    if (!currentModalFile) return;

    const currentFile = files.find((f) => f.id === currentModalFile.id);

    if (!currentFile) return;

    const pendingFile = getPendingFileByIdOrFile(
      currentFile.pendingFileId,
      currentFile.file
    );

    if (pendingFile) {
      await updateCaptionForFile(pendingFile, captionValue).catch(() => {});
    }
  }, [
    modalItems,
    files,
    pendingFileUploads,
    currentCaptionIndex,
    captionValue,
    getPendingFileByIdOrFile,
    updateCaptionForFile
  ]);

  // Wrapper for handleEditCaption to clear new files flag
  const handleEditCaptionWrapper = useCallback(
    (index: number) => {
      const clickedFile = files[index];

      if (!clickedFile) return;

      if (isShowingNewFilesOnly) {
        pendingEditFileIdRef.current = clickedFile.id;
        setIsShowingNewFilesOnly(false);
        setNewFileIds(new Set());
      } else {
        handleEditCaption(index);
      }
    },
    [files, isShowingNewFilesOnly, handleEditCaption]
  );

  const handleCaptionNext = useCallback(async () => {
    if (pendingFileUploads.length > 0) {
      const hasMoreFiles = pendingFileUploads.length > 1;

      await addPendingFile();

      if (hasMoreFiles) {
        setCaptionValue('');
      } else {
        setPendingFileUploads([]);
        onCloseCaptionModal();
      }

      return;
    }

    await handleCaptionSave();
    await captionNext();
  }, [
    handleCaptionSave,
    captionNext,
    pendingFileUploads,
    addPendingFile,
    setCaptionValue,
    onCloseCaptionModal
  ]);

  const handleCaptionPrevious = useCallback(async () => {
    await handleCaptionSave();
    await captionPrevious();
  }, [handleCaptionSave, captionPrevious]);

  const handleCaptionDone = useCallback(async () => {
    if (pendingFileUploads.length > 0) {
      await addPendingFile();
      setPendingFileUploads([]);
      onCloseCaptionModal();

      return;
    }

    await handleCaptionSave();
    setIsShowingNewFilesOnly(false);
    setNewFileIds(new Set());
    captionDone();
  }, [
    handleCaptionSave,
    captionDone,
    pendingFileUploads,
    addPendingFile,
    onCloseCaptionModal
  ]);

  /** Submit uploaded files back to the parent via callback */
  const handleSend = async () => {
    if (!rfq?.rfq?.id || files.length === 0) return;

    // Save any pending caption
    if (isCaptionModalOpen && files[currentCaptionIndex]) {
      await handleCaptionSave();
    }

    setIsSubmitting(true);

    try {
      await waitForUploads();

      const hasErrors = pendingFiles.some((pf) => pf.status === 'error');

      if (hasErrors) {
        setIsSubmitting(false);

        return;
      }

      // Collect UploadedFile objects
      const uploadedFiles = pendingFiles
        .filter((pf) => pf.status === 'completed' && pf.uploadedFile)
        .map((pf) => pf.uploadedFile!);

      setIsSubmitting(false);
      onFilesSelected(uploadedFiles);
      queryClient.invalidateQueries({
        queryKey: ['rfq', rfq.rfq.id.toString()]
      });
    } catch (error) {
      console.error('Error waiting for uploads:', error);
      setIsSubmitting(false);
    }
  };

  const isImage = (file: File) => isImageMimeType(file.type);

  // Render preview for caption modal
  const renderPreview = useCallback(
    (file: FileWithCaption, previewUrl: string | undefined) => {
      if (!previewUrl) return null;

      if (isImage(file.file)) {
        return (
          <ImagePreview
            src={previewUrl}
            alt={file.file.name}
            onError={() => {}}
          />
        );
      } else {
        return <FilePreview file={file.file} previewUrl={previewUrl} />;
      }
    },
    []
  );

  const getPreviewUrl = useCallback(
    (file: FileWithCaption) => {
      return getPreview(file.id) || file.preview;
    },
    [getPreview]
  );

  // Use full screen modal for non-image files
  const isFullScreenPreview = useMemo(() => {
    const currentFile = modalItems[currentCaptionIndex];

    if (!currentFile) return false;

    return !isImage(currentFile.file);
  }, [modalItems, currentCaptionIndex]);

  // Show warning in caption overlay if the current file can't be processed for RFQ creation
  const currentItemWarning = useMemo(() => {
    const currentFile = modalItems[currentCaptionIndex];

    if (!currentFile) return null;

    return isUnprocessableForRfqExtraction(currentFile.file.name) ? (
      <UnprocessableFileWarning variant="block" />
    ) : null;
  }, [modalItems, currentCaptionIndex]);

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 px-4 md:px-8 py-4 md:py-6 gap-4 md:gap-8 flex flex-col md:grid md:grid-cols-10 overflow-hidden">
        {/* Left side - file list */}
        <div className="flex flex-col gap-3 md:gap-4 md:col-span-4 flex-shrink-0">
          <div>
            <Typography as="h2" variant="h5" weight="bold">
              Share your references
            </Typography>
            <Typography variant="body-2" className="text-secondary-text mt-1">
              Add any supporting files you have.
            </Typography>
          </div>

          <div
            style={{
              backgroundImage: `
                linear-gradient(to right, rgba(59, 130, 246, 0.05) 1px, transparent 1px),
                linear-gradient(to bottom, rgba(59, 130, 246, 0.05) 1px, transparent 1px)
              `,
              backgroundSize: '40px 40px',
              backgroundColor: '#FFF'
            }}
            className="flex-1 min-h-[120px] max-h-[180px] md:min-h-[420px] md:max-h-[420px] overflow-y-auto cavela-scrollbar"
          >
            {files.length > 0 && (
              <div className="flex flex-col gap-3">
                <AnimatePresence>
                  {files.map((fileWithCaption, index) => {
                    const currentModalItem = modalItems[currentCaptionIndex];
                    const isSelected =
                      isCaptionModalOpen &&
                      currentModalItem &&
                      currentModalItem.id === fileWithCaption.id;

                    const pendingFile = fileWithCaption.pendingFileId
                      ? getPendingFile(fileWithCaption.pendingFileId)
                      : undefined;

                    const uploadStatusIndicator =
                      pendingFile?.status === 'uploading' ? (
                        <div className="mt-1">
                          <Progress value={pendingFile.progress} size="sm" />
                        </div>
                      ) : pendingFile?.status === 'error' ? (
                        <UploadFailedNotice
                          error={pendingFile.error}
                          onRetry={() => retryUpload(pendingFile.id)}
                          onRemove={() => handleRemoveFile(index)}
                        />
                      ) : null;

                    return (
                      <motion.div
                        key={fileWithCaption.id}
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        layout
                        transition={{
                          layout: {
                            duration: 0.3,
                            ease: [0.4, 0, 0.2, 1]
                          },
                          opacity: { duration: 0.2, ease: 'easeOut' },
                          y: { duration: 0.2, ease: 'easeOut' }
                        }}
                        className={cn(
                          'group flex items-center gap-4 p-3 bg-gray-1 rounded-xl cursor-pointer hover:bg-gray-2 transition-colors overflow-hidden',
                          isSelected && 'bg-gray-4'
                        )}
                        onClick={() => handleEditCaptionWrapper(index)}
                      >
                        {isImage(fileWithCaption.file) ? (
                          <img
                            src={fileWithCaption.preview}
                            alt={fileWithCaption.file.name}
                            className="w-12 h-12 object-cover rounded-lg"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-gray-2 group-hover:bg-gray-4 rounded-lg flex items-center justify-center transition-colors">
                            <DocumentIcon className="size-6 group-hover:text-primary transition-colors text-gray-500" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          {fileWithCaption.caption ? (
                            <Typography variant="body-2" weight="medium">
                              {fileWithCaption.caption}
                            </Typography>
                          ) : (
                            <Typography
                              variant="body-2"
                              className="text-secondary-text"
                            >
                              No caption
                            </Typography>
                          )}
                          <Typography
                            variant="body-2"
                            className="text-secondary-text truncate"
                          >
                            {fileWithCaption.file.name}
                          </Typography>

                          {isUnprocessableForRfqExtraction(
                            fileWithCaption.file.name
                          ) && <UnprocessableFileWarning />}

                          {uploadStatusIndicator}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFile(index);
                          }}
                          className="flex-shrink-0 p-1.5 hover:bg-gray-3 rounded-full transition-colors"
                          aria-label="Remove file"
                        >
                          <XMarkIcon className="size-4 text-gray-600" />
                        </button>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between flex-shrink-0">
            <button
              onClick={onClose}
              className="flex items-center gap-2 text-gray-600 hover:text-dark transition-colors"
            >
              <Typography variant="body-2" weight="bold">
                Cancel
              </Typography>
            </button>

            <ButtonV2
              onPress={handleSend}
              isDisabled={
                files.length === 0 || isSubmitting || isUploadingFiles
              }
              isLoading={isSubmitting}
              size="md"
              className="w-24 md:w-28 rounded-full px-3 md:px-4"
            >
              Submit
            </ButtonV2>
          </div>
        </div>

        {/* Right side wrapper - non-scrolling positioning context for the overlay */}
        <div className="relative md:col-span-6 overflow-hidden md:flex-1 min-h-0">
          {/* Scrollable drag and drop zone */}
          <div
            className="flex flex-col gap-4 h-full overflow-auto px-4 pt-2"
            style={{
              backgroundImage: `
                linear-gradient(to right, rgba(59, 130, 246, 0.05) 1px, transparent 1px),
                linear-gradient(to bottom, rgba(59, 130, 246, 0.05) 1px, transparent 1px)
              `,
              backgroundSize: '40px 40px',
              backgroundColor: '#FFF'
            }}
          >
            <div
              className={`min-h-[200px] md:min-h-[300px] cursor-pointer flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-4 p-6 md:p-12 transition-all duration-300 ${
                isDragging
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-4 bg-gray-1'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="bg-gray-200 size-14 flex items-center justify-center rounded-full mb-4">
                <ArrowUpTrayIcon className="size-5 text-gray-600" />
              </div>
              <Typography variant="h6" weight="bold" className="mb-1">
                Choose files to upload
              </Typography>
              <Typography variant="body-2" className="text-secondary-text">
                We support PDF, DOC, DOCX, XLSX, AI, and images
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileInputChange}
                className="hidden"
              />
            </div>
          </div>

          {/* Inline Caption Overlay - sits outside scroll container, stays fixed on top */}
          <InlineCaptionOverlay
            placeholder="What should we take from this file?"
            isOpen={isCaptionModalOpen}
            onClose={() => {
              if (pendingFileUploads.length > 0) {
                pendingFileUploads.forEach((file) => {
                  if (file.id) {
                    removePreview(file.id);
                  }
                });
                setPendingFileUploads([]);
                setCaptionValue('');
              }
              setIsShowingNewFilesOnly(false);
              setNewFileIds(new Set());
              onCloseCaptionModal();
            }}
            items={modalItems}
            currentIndex={currentCaptionIndex}
            captionValue={captionValue}
            onCaptionChange={setCaptionValue}
            isEditMode={isEditMode}
            onSave={handleCaptionSave}
            onNext={handleCaptionNext}
            onPrevious={handleCaptionPrevious}
            onDone={handleCaptionDone}
            renderPreview={renderPreview}
            getPreviewUrl={getPreviewUrl}
            isFullScreen={isFullScreenPreview}
            warning={currentItemWarning}
          />
        </div>
      </div>
    </div>
  );
};

export default AddReferencesUploadTab;
